import { useState, useEffect, useCallback } from "react";
import { useAuth, api, API } from "@/lib/auth";
import { PageHeader, Card, Btn, Icon } from "@/components/kit";
import { toast } from "sonner";

export default function WebsiteBuilder() {
  const { user, refreshUser } = useAuth();
  const [site, setSite] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/website").then((r) => setSite(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/website/generate");
      setSite({ ...data, exists: true }); await refreshUser();
      toast.success("Your microsite is live! ₹25 deducted.");
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };

  const publicUrl = `${window.location.origin}/site/${site?.slug || ""}`;
  const c = site?.content;

  return (
    <div className="fade-up">
      <PageHeader demo={false} title="MFD Website Builder" subtitle="One click — AI builds a professional microsite from your profile." icon="Globe" color="#3B82F6"
        action={<Btn onClick={generate} disabled={busy} data-testid="website-generate-btn"><Icon name="Sparkles" size={16} /> {busy ? "Building…" : site?.exists ? "Regenerate" : "Build My Website with AI"}</Btn>} />

      {!site?.exists ? (
        <Card className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl grad-blue-violet grid place-items-center mx-auto mb-4"><Icon name="Globe" size={30} className="text-white" /></div>
          <h3 className="font-head text-xl font-semibold">No website yet</h3>
          <p className="text-gray-500 mt-1 max-w-md mx-auto">MFDX AI will generate About, Services, Education and a booking CTA from your profile — SEBI/AMFI compliant. Cost: 25 credits.</p>
          <Btn onClick={generate} disabled={busy} className="mt-5 mx-auto" data-testid="website-generate-empty-btn"><Icon name="Sparkles" size={16} /> {busy ? "Building…" : "Build My Website with AI"}</Btn>
        </Card>
      ) : (
        <>
          <Card className="mb-5 flex flex-wrap items-center gap-3">
            <Icon name="Link2" size={18} className="text-blue-500" />
            <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="website-public-link" className="text-blue-600 font-medium truncate">{publicUrl}</a>
            <div className="ml-auto flex gap-2">
              <Btn variant="outline" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); }}><Icon name="Copy" size={14} /> Copy</Btn>
              <Btn variant="soft" onClick={() => window.open(publicUrl, "_blank")}><Icon name="ExternalLink" size={14} /> View live</Btn>
            </div>
          </Card>

          {/* Live preview */}
          <div className="card-mfx overflow-hidden">
            <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 text-xs text-gray-400"><span className="w-2.5 h-2.5 rounded-full bg-red-400" /><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /><span className="w-2.5 h-2.5 rounded-full bg-green-400" /><span className="ml-2">{publicUrl}</span></div>
            <MicrositeView site={site} />
          </div>
        </>
      )}
    </div>
  );
}

export function MicrositeView({ site }) {
  const c = site.content;
  return (
    <div className="bg-white">
      <div className="grad-blue-violet text-white p-10 md:p-14">
        <span className="text-xs uppercase tracking-widest opacity-80">Mutual Fund Distributor{site.arn ? ` · ${site.arn}` : ""}</span>
        <h1 className="font-head text-4xl md:text-5xl font-extrabold mt-2">{site.name}</h1>
        <p className="text-lg text-white/90 mt-3 max-w-2xl">{c.tagline}</p>
        <a href={site.slug ? `/book/${site.slug}` : "#"} className="inline-flex items-center gap-2 mt-6 bg-white text-blue-600 rounded-full px-6 py-3 font-semibold hover-lift" data-testid="microsite-book-cta"><Icon name="CalendarPlus" size={18} /> {c.cta || "Book a meeting"}</a>
      </div>
      <div className="p-10 md:p-14 grid md:grid-cols-2 gap-10">
        <div>
          <h2 className="font-head text-2xl font-bold mb-3">About</h2>
          <p className="text-gray-600 leading-relaxed">{c.about}</p>
          <div className="flex items-center gap-4 mt-4 text-sm text-gray-400"><span className="flex items-center gap-1"><Icon name="MapPin" size={14} /> {site.location}</span></div>
        </div>
        <div>
          <h2 className="font-head text-2xl font-bold mb-3">Mutual Fund Education</h2>
          <ul className="space-y-2">{(c.education || []).map((e) => <li key={e} className="flex items-center gap-2 text-gray-600"><Icon name="BookOpen" size={15} className="text-blue-500" /> {e}</li>)}</ul>
        </div>
      </div>
      <div className="px-10 md:px-14 pb-14">
        <h2 className="font-head text-2xl font-bold mb-5">Services</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {(c.services || []).map((s) => (
            <div key={s.title} className="card-mfx p-5">
              <div className="w-9 h-9 rounded-xl bg-blue-50 grid place-items-center mb-3"><Icon name="CheckCircle2" size={18} className="text-blue-600" /></div>
              <h3 className="font-head font-semibold">{s.title}</h3>
              <p className="text-sm text-gray-500 mt-1">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-gray-50 px-10 py-6 text-center text-xs text-gray-400">
        Mutual fund investments are subject to market risks. Read all scheme related documents carefully. · Built with MFDX
      </div>
    </div>
  );
}
